import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { usePopupLogin } from '../../hooks/usePopupLogin';
import { CheckCircle } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { useCompanyStore } from '@/store/useCompanyStore';
import { authService } from '@/services/authService';
import { toastUtils } from '@/common/utils/toastUtils';
import { useNavigationStore } from '@/common/store';
import { companyService, RegisterUserAndCompanyData, RegisterCompanyData } from '@/services/companyService';
import { extractDate } from '@/common/utils/dateFormat';
import Config from '@/config';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { commonService } from '@/services/commonService';
import { removeSpaces } from '@/common/utils/commonUtil';
import { colorTokens, semanticColors } from '@/common/styles/colorTokens';
import { InvitationLayout } from '@/pages/invitation/layout/InvitationLayout';
import { StepItemType } from '@/common/components/step/Step';
import Avatar from '@/common/components/avatar/Avatar';
import { typography } from '@/common/styles/typographyTokens';
import ViewSwitcher from '@/common/components/viewSwitcher/ViewSwitcher';
import { LabelInput } from '@/common/components/input/LabelInput';
import { Button } from '@/common/components/button/Button';
import { AICompanyInfoModal } from './components/AICompanyInfoModal';
import { FileUploadModal } from './components/FileUploadModal';
import { AILoadingModal } from './components/AILoadingModal';
import { aiService } from '@/services/aiService';
import Section from '@/common/components/section/Section';
import { companyChannelService } from '@/services/companyChannelService';
import LoadingModal from '@/common/components/modal/LoadingModal';
import { fileService } from '@/services/fileService';

enum ChannelApplicationStep {
  INITIAL,
  SIGNUP,
  COMPANY,
  WELCOME
}

// WelcomeStep 컴포넌트를 외부로 분리
interface WelcomeStepProps {
  companyCode: string;
  navigate: ReturnType<typeof useNavigate>;
  channelId: string;
  requestUserType?: 'MEMBER_AND_HASCOMPANY' | 'MEMBER_AND_NOCOMPANY' | 'NO_MEMBER_AND_NOCOMPANY';
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ 
  companyCode, 
  navigate, 
  channelId,
  requestUserType
}) => {
  const navigateToNext = useCallback(() => {
    // 채널 기반 지원 완료 시 ColdCall.tsx로 이동
    window.location.replace(`${Config.startupClientUrl}/${companyCode}/cold-call?channelId=${channelId}`);
  }, [companyCode, channelId]);
  
  // 이미 회사가 있는 사용자의 경우 바로 리다이렉트
  useEffect(() => {
    if (requestUserType === 'MEMBER_AND_HASCOMPANY') {
      navigateToNext();
    }
  }, [requestUserType, navigateToNext]);
  
  // 회원가입 완료 후에만 환영 메시지 표시
  if (requestUserType === 'MEMBER_AND_HASCOMPANY') {
    return (
      <Section
        title="지원 양식으로 이동 중..."
        description="잠시만 기다려주세요."
      >
        <WelcomeContent>
          <LoadingSpinner>
            <div className="spinner"></div>
          </LoadingSpinner>
        </WelcomeContent>
      </Section>
    );
  }
  
  return (
    <Section
      title="환영합니다!"
      description="회원가입이 완료되었습니다. 아래 버튼을 클릭하여 지원 양식 작성으로 넘어가세요."
    >
      <WelcomeContent>
        <SuccessAnimation>
          <CheckCircle size={64} color="#22C55E" />
        </SuccessAnimation>
        <Button onClick={navigateToNext} $size='sm'>
          지원 양식 작성하기
        </Button>
      </WelcomeContent>
    </Section>
  );
};

// OwnChannelErrorPage 전용 스타일드 컴포넌트들
const ErrorPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;

  .error-section {
    max-width: 500px;
    width: 100%;
  }
`;

const ErrorActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
`;

const OwnChannelErrorPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate('/');
    } catch (error) {
      toastUtils.error('로그아웃 중 오류가 발생했습니다.');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <ErrorPageContainer>
      <Section
        type="emphasized"
        title="접근 제한"
        description="자신의 채널에는 지원할 수 없습니다. 다른 투자사의 채널을 통해 지원해주세요."
        className="error-section"
      >
        <ErrorActions>
          <Button 
            $variant="outline" 
            $size="md" 
            onClick={handleGoBack}
          >
            뒤로가기
          </Button>
          <Button 
            $variant="fill" 
            $size="md" 
            onClick={handleLogout}
          >
            로그아웃
          </Button>
        </ErrorActions>
      </Section>
    </ErrorPageContainer>
  );
};

const ApplicationChanngelPage: React.FC = () => {
  const navigate = useNavigate();
  const {  } = useNavigationStore();
  const { user } = useAuthStore();
  const { companies } = useCompanyStore();

  // 채널 기반 지원 관련 상태 추가
  const { channelId: channelIdParam } = useParams<{ channelId: string }>();
  const [isChannelApplication, setIsChannelApplication] = useState<boolean>(false);
  
  const [requestUserType, setRequestUserType] = useState<'MEMBER_AND_HASCOMPANY' | 'MEMBER_AND_NOCOMPANY' | 'NO_MEMBER_AND_NOCOMPANY' | undefined>(undefined);

  // 상태 관리
  const [currentStep, setCurrentStep] = useState<ChannelApplicationStep>(ChannelApplicationStep.INITIAL);
  const [isPageNotFound, setIsPageNotFound] = useState<boolean>(false);
  const [isOwnChannelError, setIsOwnChannelError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // 회원가입 관련 상태
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);
  const [certifyData, setCertifyData] = useState<InisisAuthResult | null>(null);
  
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  
  // 회사 정보 관련 상태
  const [companyId, setCompanyId] = useState<number|undefined>(undefined);
  const [companyDataErrors, setCompanyDataErrors] = useState<Record<string, string>>({});
  const [isCompanyEmail, setIsCompanyEmail] = useState<boolean>(true);
  
  // AI 모달 관련 상태
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState<boolean>(false);
  const [isAILoadingModalOpen, setIsAILoadingModalOpen] = useState<boolean>(false);
  
  // AI 분석으로 채워진 필드들을 추적하는 상태
  const [aiFilledFields, setAiFilledFields] = useState<Record<string, boolean>>({});
  const [isCertifiedCompany, setIsCertifiedCompany] = useState<boolean>(false);
  
  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyNameEn: '',
    ceoName: '',
    businessNumber: '',
    corporateNumber: '',
    establishedDate: '',
    ceoEmail: '',
    companyEmail: '',
    companyCode: ''
  });

  // 기존 상태 관리 부분에 추가
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    passwordConfirm: '',
    userDi: '',
    userCi: '',
    token: ''
  });

  // 이메일 입력 값들을 별도로 관리
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  
  // AI 필드에 사용할 색상
  const aiFieldColor = colorTokens.brand[800];

  // 로그인 팝업
  const { openPopup } = usePopupLogin({
    onSuccess: async (data) => {
      setIsLoading(true);
      try {
        const responseUser = await authService.setUserProfile();
        
        if(!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }

        // localStorage에서 회사 정보 확인 (setUserProfile 후 업데이트된 정보 사용)
        const updatedCompanies = useCompanyStore.getState().companies;
        
        if(updatedCompanies && updatedCompanies.length > 0) {
          // 로그인 후에는 initChannelApplication에서 처리하도록 위임
          // 자동 리다이렉트 로직 제거 - initChannelApplication에서 처리
          console.log('로그인 완료, initChannelApplication에서 처리 예정');
        } else {
          // 회사가 없는 경우 회사 등록 단계로
          setRequestUserType('MEMBER_AND_NOCOMPANY');
          setCurrentStep(ChannelApplicationStep.COMPANY);
        }
      } catch (error) {
        console.error('로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    }
  });

  // 자기 자신의 채널인지 확인하는 함수 (localStorage 회사 정보 이용)
  const checkIfOwnChannel = async (): Promise<boolean> => {
    try {
      if (!channelIdParam) return false;
      
      // localStorage에서 회사 정보 가져오기
      const userCompanies = companies.map(companyUser => companyUser.company);
      if (!userCompanies || userCompanies.length === 0) {
        return false;
      }
      
      // 채널 정보를 가져오기 (channelIdParam이 숫자인지 문자열인지에 따라 처리)
      let channelInfo = null;
      
      // 숫자로 변환 가능한 경우 ID로 조회, 아니면 코드로 조회
      if (!isNaN(Number(channelIdParam))) {
        channelInfo = await companyChannelService.getChannelById(Number(channelIdParam));
      } else {
        channelInfo = await companyChannelService.getChannelByCode(channelIdParam);
      }
      
      if (!channelInfo || !channelInfo.company) {
        console.error('채널 정보를 찾을 수 없습니다.');
        return false;
      }
      
      // 사용자의 회사 중 하나가 채널 소유회사와 일치하는지 확인
      const channelOwnerCompanyId = channelInfo.company.id;
      const userCompanyIds = userCompanies.map(company => company.id);
      
      return userCompanyIds.includes(channelOwnerCompanyId);
      
    } catch (error) {
      console.error('채널 소유자 확인 중 오류:', error);
      return false;
    }
  };

  // 간편인증 처리 중복 방지를 위한 state
  const [isVerifying, setIsVerifying] = useState(false);

  // 간편인증 처리
  const handleCertify = async (certifyData: InisisAuthResult) => {
    // 이미 처리 중인 경우 중복 요청 방지
    if (isVerifying) {
      console.log('인증 처리 중... 중복 요청 방지');
      return;
    }

    setIsVerifying(true);
    setIsLoading(true);
    try {
      console.log('handleCertify 시작');
      console.log('certifyData', certifyData);
      
      if(certifyData.isDuplicate) {
        console.log('중복 사용자 감지, 처리 중단');
        setIsCertifiedPersonal(false);
        return toastUtils.error('이미 가입된 유저입니다.');
      }

      console.log('모든 검증 통과, 다음 단계 진행');
      setIsCertifiedPersonal(true);
      setCertifyData(certifyData);
      setFormData(prev => ({
        ...prev,
        name: certifyData.userName,
        phone: certifyData.userPhone,
        userDi: certifyData.userDi,
        userCi: certifyData.userCi,
        token: certifyData.token
      }));

      setRequestUserType('NO_MEMBER_AND_NOCOMPANY');
      setCurrentStep(ChannelApplicationStep.SIGNUP);
    } catch (error) {
      console.error('인증 처리 중 오류 발생:', error);
      toastUtils.error('인증 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      setIsVerifying(false);
    }
  };

  // 비밀번호 유효성 검사
  const validatePassword = (password: string): boolean => {
    // 최소 4자 이상
    const isValid = password.length >= 4;
    
    if (!isValid) {
      setPasswordError('비밀번호는 4자 이상이어야 합니다.');
    } else {
      setPasswordError('');
    }
    
    return isValid;
  };


  //중복 정보 유효성 검사
  const validateDuplicateInfo = async (type: string, value: string) => {
    const response = await commonService.checkDuplicateValue(type, value);

    if(type === 'companyCode') {
      response.exists && setCompanyDataErrors(prev => ({ ...prev, companyCode: '이미 등록된 회사 코드입니다.' }));
    } else if(type === 'email') {
      response.exists && setCompanyDataErrors(prev => ({ ...prev, companyEmail: '이미 등록된 이메일입니다.' }));
    } else if(type === 'businessNumber') {
      response.exists && setCompanyDataErrors(prev => ({ ...prev, businessNumber: '이미 등록된 사업자번호입니다.' }));
    }
  };

  // AI 모달 핸들러 업데이트
  const handleAIModalOpen = () => {
    // setIsAIModalOpen(true);
  };

  const handleAIModalClose = () => {
    setIsAIModalOpen(false);
  };

  const handleAIConfirm = () => {
    // AI 기능 사용 - 파일 업로드 모달 열기
    setIsAIModalOpen(false);
    setIsFileUploadModalOpen(true);
  };

  const handleManualConfirm = () => {
    // 수동 입력 로직 구현
    setIsAIModalOpen(false);
    // 현재 페이지에서 계속 진행
    toastUtils.info('수동으로 기업 정보를 입력합니다.');
  };

  // 파일 업로드 모달 핸들러
  const handleFileUploadModalClose = () => {
    setIsFileUploadModalOpen(false);
  };

  const handleFileUpload = async (file: File) => {
    setIsFileUploadModalOpen(false);
    setIsAILoadingModalOpen(true);

    try {
      const fileResult = await fileService.uploadFile([file]);

      if(!fileResult) return toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');

      const response = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id
      });
      
      if (response) {
        // 채널 지원의 경우 사업자번호 비교 없이 바로 필드 업데이트
        const updatedCompanyData = {
          ...companyData,
          companyName: response.companyName,
          ceoName: response.representative,
          businessNumber: formatBusinessNumber(response.businessNumber),
          corporateNumber: formatCorporateNumber(response.corporateNumber),
          establishedDate: formatFoundingDate(response.establishmentDate)
        };

        setCompanyData(updatedCompanyData);
        
        // AI로 채워진 필드들 표시
        setAiFilledFields({
          companyName: true,
          ceoName: true,
          businessNumber: true,
          corporateNumber: true,
          establishedDate: true
        });

        setIsAILoadingModalOpen(false);
        toastUtils.success('AI 분석이 완료되어 기업 정보가 자동으로 입력되었습니다.');
        
      } else {
        throw new Error('AI 분석 실패');
      }
    } catch (error) {
      console.error('AI 분석 중 오류 발생:', error);
      toastUtils.error('AI 분석 중 오류가 발생했습니다. 수동으로 입력해주세요.');
      setIsAILoadingModalOpen(false);
    }
  };

  // AI 로딩 모달 핸들러
  const handleAILoadingModalClose = () => {
    setIsAILoadingModalOpen(false);
  };

  // AI 채움 상태 리셋 함수
  const handleFieldChange = (fieldName: string, value: string) => {
    setCompanyData(prev => ({ ...prev, [fieldName]: value }));
    // 사용자가 값을 변경하면 해당 필드의 AI 채움 상태를 false로 변경
    if (aiFilledFields[fieldName]) {
      setAiFilledFields(prev => ({ ...prev, [fieldName]: false }));
      setIsCertifiedCompany(false);
    }
  };

  // 포맷팅 헬퍼 함수들
  const formatBusinessNumber = (businessNumber: string) => {
    const cleanNumber = businessNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 10) {
      return `${cleanNumber.slice(0,3)}-${cleanNumber.slice(3,5)}-${cleanNumber.slice(5)}`;
    }
    return businessNumber;
  };

  const formatCorporateNumber = (corporateNumber: string) => {
    const cleanNumber = corporateNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 13) {
      return `${cleanNumber.slice(0, 6)}-${cleanNumber.slice(6)}`;
    }
    return corporateNumber;
  };

  const formatFoundingDate = (foundingDate: string) => {
    // YYYYMMDD 형식을 YYYY-MM-DD로 변환
    const cleanDate = foundingDate.replace(/[^0-9]/g, '');
    if (cleanDate.length === 8) {
      return `${cleanDate.slice(0,4)}-${cleanDate.slice(4,6)}-${cleanDate.slice(6)}`;
    }
    return foundingDate;
  };

  const checkCompanyStatus = async () => {
    const response = await companyService.checkCompanyStatus({
      companyName: companyData.companyName,
      ceoName: companyData.ceoName,
      businessNumber: companyData.businessNumber.replace(/-/g, ''),
      corporateNumber: companyData.corporateNumber.replace(/-/g, ''),
      establishedDate: extractDate(companyData.establishedDate).replace(/-/g, '')
    });
    if (response.isExistCompany) {
      if (!isCertifiedCompany) {
        toastUtils.success('기업 정보 인증이 완료되었습니다.');
      }
      setIsCertifiedCompany(true);
    } else {
      toastUtils.error('기업 정보 인증이 불가합니다. 입력한 정보를 확인하시거나 잠시 후 다시 시도하세요.');
    }
    return response;
  };

  // 회사 정보 입력 완료
  const handleCompanyComplete = async () => {

    setIsLoading(true);
    try {
      const response = await checkCompanyStatus();

      if (response.isExistCompany) {
        // 자기 자신의 채널인지 확인 (회원가입/회사등록 완료 후)
        if(requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
          // 회원가입 시에는 아직 회사가 등록되지 않았으므로 체크하지 않음
        } else if(requestUserType === 'MEMBER_AND_NOCOMPANY') {
          // 기존 회원이 회사 등록하는 경우에만 체크
          if(user && companies && companies.length > 0) {
            const isOwnChannel = await checkIfOwnChannel();
            
                      if(isOwnChannel) {
            setIsOwnChannelError(true);
            return;
          }
          }
        }

        //회원 가입
        if(requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
          const bodyData:RegisterUserAndCompanyData = {
            notificationId: '', // 채널 지원의 경우 빈 문자열
            companyEmail: companyData.companyEmail,
            companyEmailVerified: true,
            companyCode: companyData.companyCode,
            isVerified: true,
            isPreStartup: false,
            isCeo: formData.name === companyData.ceoName,
            companyName: companyData.companyName,
            companyNameEn: companyData.companyNameEn || undefined,
            businessNumber: companyData.businessNumber,
            corporateNumber: companyData.corporateNumber,
            isBusinessVerified: true,
            foundingDate: formatFoundingDate(companyData.establishedDate),
            ceoName: companyData.ceoName,
            ceoEmail: companyData.ceoEmail,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            emailVerified: true,
            password: formData.password,
            userDi: formData.userDi,
            userCi: formData.userCi,
            token: formData.token
          }
          const response = await companyService.registerUserAndCompany(bodyData);
            if(response.success) {
              setCurrentStep(ChannelApplicationStep.WELCOME);
            } else {
              toastUtils.error(response.message || '회원 가입 중 오류가 발생했습니다.');
            }
        }else if(requestUserType === 'MEMBER_AND_NOCOMPANY') {
          //회사 등록
          const bodyData:RegisterCompanyData = {
            notificationId: '', // 채널 지원의 경우 빈 문자열
            companyEmail: companyData.companyEmail,
            companyEmailVerified: true,
            companyCode: companyData.companyCode,
            isVerified: true,
            isPreStartup: false,
            isCeo: user?.name === companyData.ceoName,
            companyName: companyData.companyName,
            companyNameEn: companyData.companyNameEn || undefined,
            businessNumber: companyData.businessNumber,
            corporateNumber: companyData.corporateNumber,
            isBusinessVerified: true,
            foundingDate: formatFoundingDate(companyData.establishedDate),
            ceoName: companyData.ceoName,
            ceoEmail: companyData.ceoEmail
          }
          const response = await companyService.registerCompany(bodyData);
          if(response.success) {
            setCurrentStep(ChannelApplicationStep.WELCOME);
          } else {
            toastUtils.error(response.message || '회사 등록 중 오류가 발생했습니다.');
          }
        }
        
      } else {
        toastUtils.info('확인할 수 없는 회사입니다.');
      }
    } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || '회사 정보 확인 중 오류가 발생했습니다.';
        toastUtils.error(errorMessage);
      } finally {
      setIsLoading(false);
    }
  };
  // 초기화 함수 - 채널 지원용
  const initChannelApplication = async () => {
    try {
      // 이미 에러 상태인 경우 처리하지 않음
      if (isOwnChannelError) {
        return;
      }
      
      if(user) {
        console.log('user 있음');
        console.log('user', user);
        
        // localStorage에서 회사 정보 확인
        if(companies && companies.length > 0) {
          // 자기 자신의 채널인지 확인
          const isOwnChannel = await checkIfOwnChannel();
          
          if(isOwnChannel) {
            setIsOwnChannelError(true);
            return;
          }
          
          // 자동 리다이렉트 제거 - 사용자가 직접 진행하도록 변경
          // const firstCompany = companies[0].company;
          // window.location.replace(`${Config.startupClientUrl}/${firstCompany.code}/cold-call?channelId=${channelIdParam}`);
          
          // 대신 웰컴 단계로 이동
          setRequestUserType('MEMBER_AND_HASCOMPANY');
          setCurrentStep(ChannelApplicationStep.WELCOME);
        } else {
          // 회사가 없는 경우 회사 등록 단계로
          setRequestUserType('MEMBER_AND_NOCOMPANY');
          setCurrentStep(ChannelApplicationStep.COMPANY);
        }
      } else {      
        setCurrentStep(ChannelApplicationStep.INITIAL);
      }  
    } catch (error) {
      console.error('초기화 중 오류 발생:', error);
      toastUtils.error('페이지 초기화 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    if (isChannelApplication && user !== undefined) {
      initChannelApplication();
    }
  }, [isChannelApplication, user]);

  useEffect(() => {
    // 페이지 진입 즉시 네비게이션 숨김
    
    
    // 채널 지원의 경우 바로 채널 정보 설정
    if (channelIdParam) {
      setIsChannelApplication(true);
      setCurrentStep(ChannelApplicationStep.INITIAL);
    } else {
      setIsPageNotFound(true);
    }
    
    // 컴포넌트 언마운트 시에도 네비게이션 상태 유지
    return () => {
      // 페이지를 떠날 때도 네비게이션 숨김 상태 유지
      
    };
  }, [  channelIdParam]);

  // Navigation 강제 숨김을 위한 추가 useEffect
  useEffect(() => {
    // 컴포넌트가 마운트되는 동안 계속 네비게이션 숨김 상태 유지
    const hideNavInterval = setInterval(() => {
      
    }, 100);

    return () => {
      clearInterval(hideNavInterval);
    };
  }, []);

  useEffect(() => {
    if (currentStep === ChannelApplicationStep.COMPANY) {
      handleAIModalOpen();
    }
  }, [currentStep]);

  // AI로 채워진 필드들이 모두 true일 때 자동으로 회사 상태 확인
  useEffect(() => {
    const allAIFieldsFilled = aiFilledFields.companyName && 
                              aiFilledFields.ceoName && 
                              aiFilledFields.businessNumber && 
                              aiFilledFields.corporateNumber && 
                              aiFilledFields.establishedDate;
    
    if (allAIFieldsFilled && companyData.companyName && companyData.ceoName && 
        companyData.businessNumber && companyData.corporateNumber && companyData.establishedDate) {
      const autoCheckCompanyStatus = async () => {
        try {
          const statusResponse = await companyService.checkCompanyStatus({
            companyName: companyData.companyName,
            ceoName: companyData.ceoName,
            businessNumber: companyData.businessNumber.replace(/-/g, ''),
            corporateNumber: companyData.corporateNumber.replace(/-/g, ''),
            establishedDate: extractDate(companyData.establishedDate).replace(/-/g, '')
          });
          if(statusResponse.isExistCompany) {
            setIsCertifiedCompany(true);
          }
        } catch (error) {
          console.error('자동 회사 상태 확인 중 오류:', error);
        }
      };
      
      autoCheckCompanyStatus();
    }
  }, [aiFilledFields, companyData.companyName, companyData.ceoName, companyData.businessNumber, companyData.corporateNumber, companyData.establishedDate]);



  if (isPageNotFound) return <div>페이지를 찾을 수 없습니다.</div>;
  if (isOwnChannelError) return <OwnChannelErrorPage />;

  // 기존 steps 정의 부분을 수정
  const getSteps = (): StepItemType[] => {
    // 채널 지원용 스텝
    if (isChannelApplication) {
      // 초기 화면에서는 첫 단계만 표시
      if (requestUserType === undefined) {
        return [
          { id: ChannelApplicationStep.INITIAL, label: '시작하기', status: 'current' }
        ];
      }

      // 회원가입 후 진행 시
      if (requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
        return [
          { id: ChannelApplicationStep.INITIAL, label: '시작하기', status: 'completed' },
          { id: ChannelApplicationStep.SIGNUP, label: '작성자 정보', status: 'completed' },
          { id: ChannelApplicationStep.COMPANY, label: '기업 인증', status: 'current' },
          { id: ChannelApplicationStep.WELCOME, label: '인증 완료', status: 'pending' }
        ];
      }

      // 로그인 후 회사 없음
      if (requestUserType === 'MEMBER_AND_NOCOMPANY') {
        return [
          { id: ChannelApplicationStep.INITIAL, label: '시작하기', status: 'completed' },
          { id: ChannelApplicationStep.COMPANY, label: '기업 인증', status: 'current' },
          { id: ChannelApplicationStep.WELCOME, label: '인증 완료', status: 'pending' }
        ];
      }

      // 로그인 후 회사 있음
      if (requestUserType === 'MEMBER_AND_HASCOMPANY') {
        return [
          { id: ChannelApplicationStep.INITIAL, label: '시작하기', status: 'completed' },
          { id: ChannelApplicationStep.WELCOME, label: '인증 완료', status: 'current' }
        ];
      }

      // 기본값 (채널 지원 단계)
              return [
          { id: ChannelApplicationStep.INITIAL, label: '시작하기', status: 'current' }
        ];
    }

    // 기존 초대 기반 로직은 제거됨
    return [];
  };

  // Step 1: 초기 화면
  const InitialStep = () => (
    <Section
      title="시작하기" // 채널 지원 대신 시작하기로 변경
      description={`간편인증이 필요합니다. 팩트시트 기존 회원이라면 로그인을 통해 더욱 빠르게 진행할 수 있습니다.`}
    >
      <RequestMessageContainer>
        {/* <FirstMessageRow>
          <Avatar alt={`${channelIdParam} 채널`} isUser={false}/>
          <RequestMessage>{`${channelIdParam} 채널`}에 지원하기 위해 회원가입 또는 로그인을 진행해주세요.</RequestMessage>
        </FirstMessageRow>
        <RequestMessage>계속 진행하시려면 간편 인증으로 회원가입하시거나 기존 계정으로 로그인해주세요.</RequestMessage> // 필수적이지 않은 정보 우선 삭제 */}
        <ButtonGroup>
          <InisisAuthButton 
            $variant='fill'
            onSuccess={(data) => {
              handleCertify(data);
            }}
            $title='간편 인증'
            $size='sm'
          />
          <Button 
            $variant='outline'
            onClick={openPopup}
            disabled={isLoading}
            $size='sm'
          >
            {isLoading ? '처리 중...' : 'Factsheet 로그인'}
          </Button>
        </ButtonGroup>
      </RequestMessageContainer>
    </Section>
  );

  // SignupStep 컴포넌트
  const SignupStep = () => (
    <Section
      title="작성자 정보" //회원가입으로 접근하지 않고, 단계별로 정보를 입력하는 것으로 인지하도록 유도 
      description="양식을 작성하실 분의 정보를 입력해주세요."
    >
      <SignupFormContainer>
        {/* 이름, 휴대폰 번호 행 */}
        <FormRow>
          <FormField>
            <LabelInput label='이름' required={true} value={formData.name} disabled={true} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} />
          </FormField>
          <FormField>
            <LabelInput label='휴대폰 번호' required={true} value={formData.phone} disabled={true} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
          </FormField>
        </FormRow>

        {/* 이메일 주소 행 */}
        <FormRow>
          <FormField style={{ width: '100%' }}>
            <LabelInput 
              label='이메일 주소' 
              required={true} 
              value={primaryEmail} 
              onChange={(e) => setPrimaryEmail(e.target.value)}
              placeholder="이메일 주소를 입력해주세요"
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField style={{ width: '100%' }}>
            <FieldLabel>위 이메일 주소가 회사용 계정인가요? <RequiredMark>*</RequiredMark></FieldLabel>
            <ViewSwitcher options={[{label: '네, 회사용 계정입니다.'}, {label: '아니오, 개인 계정입니다.'}]} selectedIndex={isCompanyEmail ? 0 : 1} onChange={(index) => setIsCompanyEmail(index === 0)} />
          </FormField>
        </FormRow>

        {/* 추가 이메일 주소 행 */}
        <FormRow>
          <FormField style={{ width: '100%' }}>
            <FormRow>
              <LabelInput
                label={`${isCompanyEmail ? '개인 이메일 주소' : '회사 이메일 주소'}`}
                required={false}
                type="text"
                value={secondaryEmail}
                onChange={(e) => setSecondaryEmail(e.target.value)}
                placeholder={`${isCompanyEmail ? '개인' : '회사'} 이메일을 입력해주세요`}
              />
            </FormRow>
            <InfoText>{isCompanyEmail ? '개인 이메일 계정을 함께 등록하시면, 더욱 편리하게 사용하실 수 있어요.' : '회사용 이메일 계정을 등록하시면 개인 이메일 주소를 노출하지 않고 사용하실 수 있습니다.'}</InfoText>
          </FormField>
        </FormRow>

        {/* 비밀번호, 비밀번호 확인 행 */}
        <PasswordFormRow>
          <FormField style={{ flex: 1 }}>
            <LabelInput
              label="비밀번호"
              type="password"

              value={password}
              onChange={(e) => {
                const cleanValue = removeSpaces(e.target.value);
                setPassword(cleanValue);
                validatePassword(cleanValue);
                if (passwordConfirm !== cleanValue) {
                  setPasswordError('비밀번호가 일치하지 않습니다.');
                } else {
                  setPasswordError('');
                }
              }}
              placeholder="비밀번호를 입력해주세요"
            />
          </FormField>
          <FormField style={{ flex: 1 }}>
            <LabelInput
              label="비밀번호 확인"
              type="password"

              value={passwordConfirm}
              onChange={(e) => {
                const cleanValue = removeSpaces(e.target.value);
                setPasswordConfirm(cleanValue);
                if (password !== cleanValue) {
                  setPasswordError('비밀번호가 일치하지 않습니다.');
                } else {
                  setPasswordError('');
                }
              }}
              placeholder="비밀번호를 다시 입력해주세요"
              error={!!passwordError}
              errorMessage={passwordError || '비밀번호가 일치하지 않습니다.'}
            />
          </FormField>
        </PasswordFormRow>
        <InfoText>{'투자사의 피드백 및 진행상황을 확인하실 수 있도록 비밀번호를 입력해주세요.'}</InfoText>

      </SignupFormContainer>

      <ButtonContainer>
        <Button 
          $variant='outline'
          onClick={() => setCurrentStep(ChannelApplicationStep.INITIAL)}
          $size='sm'
        >
          이전
        </Button>
        <Button 
          onClick={() => {
            // 이메일 설정을 isCompanyEmail에 따라 결정
            if (isCompanyEmail) {
              // 첫 번째 이메일이 회사용, 두 번째가 개인용
              setCompanyData(prev => ({ ...prev, companyEmail: primaryEmail }));
              setFormData(prev => ({ ...prev, email: secondaryEmail, password: password, passwordConfirm: passwordConfirm }));
            } else {
              // 첫 번째 이메일이 개인용, 두 번째가 회사용
              setFormData(prev => ({ ...prev, email: primaryEmail, password: password, passwordConfirm: passwordConfirm }));
              setCompanyData(prev => ({ ...prev, companyEmail: secondaryEmail }));
            }
            setCurrentStep(ChannelApplicationStep.COMPANY);
          }}
          disabled={isLoading || !!passwordError || !password || !passwordConfirm || !primaryEmail}
          $size='sm'
        >
          다음
        </Button>
      </ButtonContainer>
    </Section>
  );

  // CompanyStep 컴포넌트
  const CompanyStep = () => (
    <Section
      title="기업 인증"
      description="기업 정보를 입력하고 인증을 완료해주세요."
    >

      
      <CompanyFormContainer>
        <>
            {/* 기업명, 영문 기업명 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='기업명' 
                  value={companyData.companyName}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('companyName', value)}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    if(e.target.value.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, companyName: '기업명을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { companyName, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="기업명을 입력해주세요"
                  error={!!companyDataErrors.companyName}
                  errorMessage={companyDataErrors.companyName || '기업명을 입력해주세요.'}
                  color={aiFilledFields.companyName ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='영문 기업명' 
                  value={companyData.companyNameEn}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, companyNameEn: value }))}
                  placeholder="영문 기업명을 입력해주세요"
                />
              </FormField>
            </FormRow>

            {/* 대표자명, 사업자번호 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='대표자명' 
                  value={companyData.ceoName}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('ceoName', value)}
                  onBlur={() => {
                    if(companyData.ceoName.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, ceoName: '대표자명을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { ceoName, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="대표자명을 입력해주세요"
                  error={!!companyDataErrors.ceoName}
                  errorMessage={companyDataErrors.ceoName || '대표자명을 입력해주세요.'}
                  color={aiFilledFields.ceoName ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='사업자번호' 
                  value={companyData.businessNumber}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => {
                    handleFieldChange('businessNumber', value);
                    const cleanValue = value.replace(/[^0-9]/g, '');
                    if(cleanValue.length <= 10) {
                      const formatted = cleanValue.length > 6 
                        ? `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 5)}-${cleanValue.slice(5)}` 
                        : cleanValue;
                      setCompanyData(prev => ({ ...prev, businessNumber: formatted }));
                    }
                  }}
                  placeholder="사업자번호를 입력해주세요"
                  error={!!companyDataErrors.businessNumber}
                  errorMessage={companyDataErrors.businessNumber || '사업자번호를 입력해주세요.'}
                  color={aiFilledFields.businessNumber ? aiFieldColor : undefined}
                />
              </FormField>
            </FormRow>

            {/* 법인번호, 설립일 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='법인번호' 
                  value={companyData.corporateNumber}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => {
                    // 하이픈 자동 추가 (ex: 1234567890123 -> 123456-7890123)
                    const cleanValue = value.replace(/[^0-9]/g, '');
                    if (cleanValue.length <= 13) {
                      const formatted = cleanValue.length > 6 
                        ? `${cleanValue.slice(0, 6)}-${cleanValue.slice(6)}` 
                        : cleanValue;
                      handleFieldChange('corporateNumber', formatted);
                    }
                  }}
                  onBlur={() => {
                    if(companyData.corporateNumber.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, corporateNumber: '법인번호를 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { corporateNumber, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="법인번호를 입력해주세요"
                  error={!!companyDataErrors.corporateNumber}
                  errorMessage={companyDataErrors.corporateNumber || '법인번호를 입력해주세요.'}
                  color={aiFilledFields.corporateNumber ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='설립일' 
                  type="date"
                  value={companyData.establishedDate}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('establishedDate', value)}
                  onBlur={() => {
                    if(companyData.establishedDate.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, establishedDate: '설립일을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { establishedDate, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="설립일을 입력해주세요"
                  error={!!companyDataErrors.establishedDate}
                  errorMessage={companyDataErrors.establishedDate || '설립일을 입력해주세요.'}
                  color={aiFilledFields.establishedDate ? aiFieldColor : undefined}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField style={{ alignItems: 'flex-end' }}>
                <Button $size='sm' onClick={checkCompanyStatus} $variant={isCertifiedCompany ? 'fill' : 'outline'} $isDisabled={isCertifiedCompany}>{isCertifiedCompany ? '기업 정보 인증 완료' : '기업 정보 인증하기'}</Button>
              </FormField>
            </FormRow>

            {/* 대표자 이메일 행 */}
            {formData.name !== companyData.ceoName && (
              <FormRow>
                <FormField style={{ width: '100%' }}>
                  <LabelInput
                    label='대표자 이메일'
                    value={companyData.ceoEmail}
                    onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, ceoEmail: value }))}
                    placeholder="대표자 이메일을 입력해주세요"
                    onBlur={() => {
                      if(companyData.ceoEmail.length === 0) {
                        setCompanyDataErrors(prev => ({ ...prev, ceoEmail: '대표자 이메일을 입력해주세요.' }));
                      }else{
                        setCompanyDataErrors(prev => {
                          const { ceoEmail, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    error={!!companyDataErrors.ceoEmail}
                    errorMessage={companyDataErrors.ceoEmail || '대표자 이메일을 입력해주세요.'}
                  />
                </FormField>
              </FormRow>
            )}

            {/* 회사 코드 행 */}
            <FormRow>
              <FormField>
                <FormRow>
                  <LabelInput
                    label='회사 코드'
                    value={companyData.companyCode}
                    onChangeData={(value: string) => {
                      if(value.includes(" ")) {
                        setCompanyDataErrors(prev => ({ ...prev, companyCode: '회사 코드는 공백을 포함할 수 없습니다.' }));
                      } else {
                        setCompanyDataErrors(prev => ({ ...prev, companyCode: '' }));
                      }
                      setCompanyData(prev => ({ ...prev, companyCode: value }))
                    }}
                    onBlur={() => {
                      if(companyData.companyCode.length === 0) {
                        return setCompanyDataErrors(prev => ({ ...prev, companyCode: '회사 코드를 입력해주세요.' }));
                      }
                      setCompanyDataErrors(prev => {
                        const { companyCode, ...rest } = prev;
                        return rest;
                      });
                      validateDuplicateInfo('companyCode', companyData.companyCode)
                    }}
                    placeholder="회사 코드를 입력해주세요"
                    error={!!companyDataErrors.companyCode}
                    errorMessage={companyDataErrors.companyCode || '회사 코드를 입력해주세요.'}
                  />
                </FormRow>
                <InfoText>회사에서 사용할 고유한 코드를 입력해주세요. 회사의 영문명을 권장합니다.</InfoText> 
              </FormField>
            </FormRow>
          </>
      </CompanyFormContainer>

      <ButtonContainer>
        <Button 
          onClick={handleCompanyComplete}
          disabled={
            Object.keys(companyDataErrors).length > 0 || 
            !companyData.companyName || 
            !companyData.ceoName ||
            !companyData.businessNumber ||
            !companyData.corporateNumber ||
            !companyData.establishedDate ||
            !companyData.companyCode ||
            (user?.name !== companyData.ceoName && !companyData.ceoEmail) ||
            !isCertifiedCompany
          }
          $size='sm'
        >
          {isLoading ? '처리 중...' : '완료'}
        </Button>
      </ButtonContainer>
    </Section>
  );

  const steps: StepItemType[] = getSteps().map((step, index) => {
    const currentStepIndex = getSteps().findIndex(s => s.id === currentStep);
    const stepIndex = getSteps().findIndex(s => s.id === step.id);
    const isCompleted = currentStepIndex > stepIndex;
    const isActive = step.id === currentStep;
    
    return {
      id: index + 1,
      label: step.label,
      status: (isCompleted ? 'completed' : isActive ? 'current' : 'pending') as 'current' | 'completed' | 'pending' | 'disabled'
    };
  });

  return (
    <>
      {isLoading && (
        <LoadingModal isOpen={isLoading} message="처리 중입니다..."/>
      )}

      <InvitationLayout 
        maxWidth="800px" 
        showSteps={currentStep !== ChannelApplicationStep.WELCOME}
        steps={requestUserType && steps}
      >
        {currentStep === ChannelApplicationStep.INITIAL && InitialStep()}
        {currentStep === ChannelApplicationStep.SIGNUP && SignupStep()}
        {currentStep === ChannelApplicationStep.COMPANY && CompanyStep()}
        {currentStep === ChannelApplicationStep.WELCOME && (
        <WelcomeStep 
          companyCode={
            requestUserType === 'MEMBER_AND_HASCOMPANY' && companies && companies.length > 0
              ? companies[0].company.code
              : companyData.companyCode
          } 
          navigate={navigate}
          channelId={channelIdParam || ''}
          requestUserType={requestUserType}
        />
      )}
      </InvitationLayout>

      {/* AI 기업 정보 확인 모달 */}
      <AICompanyInfoModal
        isOpen={isAIModalOpen}
        onClose={handleAIModalClose}
        onAIConfirm={handleAIConfirm}
        onManualConfirm={handleManualConfirm}
      />

      {/* 파일 업로드 모달 */}
      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        onClose={handleFileUploadModalClose}
        onFileUpload={handleFileUpload}
      />

      {/* AI 로딩 모달 */}
      <AILoadingModal
        isOpen={isAILoadingModalOpen}
        onClose={handleAILoadingModalClose}
      />
    </>
  );
};

// Styled Components
const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: left;
  flex-wrap: wrap;
`;

const SuccessAnimation = styled.div`
  margin: 0 auto 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: ${semanticColors.background.secondary};
  border-radius: 50%;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
    }
    70% {
      box-shadow: 0 0 0 15px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: ${semanticColors.text.primary};
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: ${semanticColors.text.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

// SignupStep 전용 스타일드 컴포넌트들
const SignupFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  flex: 1;
`;

const RequestMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  width: 100%;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  justify-content: space-between;
  width: 254px;
  flex-grow: 1;
  min-width: 0;
  
  /* width: 100% 스타일이 인라인으로 적용된 경우 */
  &[style*="width: 100%"] {
    width: 100% !important;
    flex: 1 1 100% !important;
    min-width: 0 !important;
  }
`;

const FieldLabel = styled.div<{ $isAIFilled?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 2px;
  height: 21px;
  flex: none;
  order: 0;
  flex-grow: 0;

  ${props => props.$isAIFilled && `
    color: ${colorTokens.brand[800]};
  `}
`;

const RequiredMark = styled.span`
  width: 7px;
  height: 21px;
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #67727A;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const InfoText = styled.div`
  width: 100%;
  height: 21px;
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: #000000;
  opacity: 0.5;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 0 0 0;
  gap: 16px;
  width: 100%;
  min-height: 40px;
  flex: none;
  align-self: stretch;
  margin-top: auto;
`;

// CompanyStep 전용 스타일드 컴포넌트들
const CompanyFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  flex: 1;
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
`;

const PasswordFormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0px;
  gap: 10px;
  width: 100%;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

export default ApplicationChanngelPage; 